import apiService from 'frontcore/data/api-service/api-service'
import { APIServiceResponse } from 'frontcore/data/api-service/types'
import { App } from 'vue'
import { AxiosRequestConfig, AxiosResponse, AxiosResponseHeaders } from 'axios'
import { getHeaders, injectHttpLibrary, requestNeedHeaders } from 'frontcore/utils/communications/apiClient'
import { HTTP_OK, HTTP_RESET_CONTENT } from 'frontcore/constants/communications'
import { SERVER_API_URL } from 'tscore/constants/server'

interface OldMleanResponse<T = unknown, D = unknown> {
    body: T
    status: number
    statusText: string
    headers: AxiosResponseHeaders
    config: AxiosRequestConfig<D>
    request?: unknown
}

type OldMleanParameters = Record<string, string | number | boolean | string[] | number[]>

const isExternalResource = (urlString: string) => {
  try {
    const url = new URL(urlString)
    return !SERVER_API_URL.includes(url.host)
  } catch {
    return false
  }
}

export async function _get (
  url: string,
  includeLanguage = true,
  showLoading = true,
  includeToken = true,
  requestConfig?: AxiosRequestConfig
): Promise<unknown> {
  if (isExternalResource(url) || requestConfig) {
    const headers = requestConfig?.headers ?? _getHeaders(url, { includeLanguage}).headers
    const response = await apiService
      .buildRequest()
      .withHeaders(headers)
      .get(url)
    return response.data
  }
  const headers = _getHeaders(url, { includeLanguage, includeToken, showLoading}).headers
  const response = await apiService
    .buildRequest()
    .withHeaders(headers)
    .withSessionToken()
    .get(url)
  const parsedResponse = __asMleanResponse(response)

  if (parsedResponse.status === HTTP_RESET_CONTENT) {
    window.history.go(0)
  }
  if (parsedResponse.status !== HTTP_OK) {
    return response
  }
  return parsedResponse.body
}

export async function _post (url: string, data: Dictionary<unknown>): Promise<OldMleanResponse> {
  const headers = _getHeaders(url).headers
  const response = await apiService
    .buildRequest()
    .withHeaders(headers)
    .withSessionToken()
    .post(url, data)
  return __asMleanResponse(response)
}

export async function _patch (url: string, data: Dictionary<unknown>): Promise<OldMleanResponse> {
  const headers = _getHeaders(url).headers
  const response = await apiService
    .buildRequest()
    .withHeaders(headers)
    .withSessionToken()
    .patch(url, data)
  return __asMleanResponse(response)
}

export async function _put (url: string, data: Dictionary<unknown>): Promise<OldMleanResponse> {
  const headers = _getHeaders(url).headers
  const response = await apiService
    .buildRequest()
    .withHeaders(headers)
    .withSessionToken()
    .put(url, data)
  return __asMleanResponse(response)
}

export async function _delete (url: string, data?: Dictionary<unknown>): Promise<OldMleanResponse> {
  const headers = _getHeaders(url).headers
  const response = await apiService
    .buildRequest()
    .withHeaders(headers)
    .withSessionToken()
    .delete(url, data)
  return __asMleanResponse(response)
}

export async function _head (url: string): Promise<OldMleanResponse> {
  const headers = _getHeaders(url).headers
  const response = await apiService
    .buildRequest()
    .withHeaders(headers)
    .withSessionToken()
    .head(url)
  return __asMleanResponse(response)
}

type HeaderConfiguration = { includeLanguage?: boolean; includeToken?: boolean; showLoading?: boolean }
function _getHeaders (urlString: string, configuration: HeaderConfiguration = {}) {
  if (isExternalResource(urlString) || !requestNeedHeaders(`${API_URL}/${urlString}`)) {
    return { headers: {}, showLoading: configuration.showLoading }
  }
    return getHeaders(configuration)

}

function __asMleanResponse (response: AxiosResponse | OldMleanResponse |  APIServiceResponse ): OldMleanResponse {
  if ('body' in response) {
    return response
  }
  const { data, ...restResponse } = response
  return {
    ...restResponse,
    body: data,
    status: response.status,
    statusText: 'statusText' in restResponse ? restResponse.statusText : '',
    config: 'config' in restResponse ? restResponse.config : {},
  }
}

export type CommunicationMethods = {
  get: typeof _get
  post: typeof _post
  patch: typeof _patch
  put: typeof _put
  delete: typeof _delete
  head: typeof _head
}

const http: CommunicationMethods = {
  get: _get,
  post: _post,
  patch: _patch,
  put: _put,
  delete: _delete,
  head: _head,
}

export function useCommunications (app: App<Element>): void {
  app.config.globalProperties.$http = http
  injectHttpLibrary(http)
}

export function getUrlQueryParameterFromObject (parameters: OldMleanParameters, strict = true): string {
  return Object.keys(parameters)
    .filter(key => strict || (parameters[key] !== null && parameters[key] !== undefined))
    .map(key => {
      let value = parameters[key]
      if (typeof value === 'boolean') {
        value = value ? 'True' : 'False'
      }
      if (Array.isArray(value)) {
        value = value.join(',')
      }
      return `${key}=${value}`
    }).join('&')
}

export function extractBodyResponse (response: OldMleanResponse): unknown {
  try {
    return 'body' in response ? response.body : response
  } catch {
    return response
  }
}

export default http
